import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FormProvider,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import { BackButton, Modal } from '@/components';
import useStatesQuery from '@/modules/core/queries/useStates.query';
import { CreatedProviderDto, CreateProviderDto, UpdatedProviderDto, UpdateProviderDto } from '@/modules/UseMyMoney/queries';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { useSubmitProviderModal } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks';
import useProviderForm, { ProviderFormField } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useProviderForm';

import ProviderModalForm from './ProviderModalForm';

interface Props {
  onSetVisible: (value: boolean) => void;
  createProvider: (createProviderDto: CreateProviderDto) => Promise<CreatedProviderDto>;
  updateProvider: (createProviderDto: UpdateProviderDto) => Promise<UpdatedProviderDto>;
  handleChangeProvider: (providerId?: string) => void;
  editableProviderId?: string;
  setEditableProviderId: (editableProviderId: string) => void;
  newProviderName?: string;
  setNewProviderName: (editableProviderId: string) => void;
}

const ProviderModal: React.FC<Props> = ({
  onSetVisible,
  createProvider,
  updateProvider,
  handleChangeProvider,
  editableProviderId,
  setEditableProviderId,
  newProviderName,
  setNewProviderName,
  }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { ocrDocument } = useDocumentOcrStore();
  const [isLoading, setIsLoading] = useState(false);
  const { states } = useStatesQuery();

  const editableProvider = useMemo(() => {
    if (!editableProviderId) return;
    return ocrDocument?.providers?.find((provider) => provider?.id === editableProviderId);
  }, [editableProviderId, ocrDocument?.providers]);

  const { methods } = useProviderForm({
    defaultValues: {
      [ProviderFormField.providerName]: editableProvider?.name || newProviderName,
      [ProviderFormField.addressLine1]: editableProvider?.address?.line1,
      [ProviderFormField.addressLine2]: editableProvider?.address?.line2,
      [ProviderFormField.city]: editableProvider?.address?.city,
      [ProviderFormField.state]: editableProvider?.address?.state?.id,
      [ProviderFormField.zipCode]: editableProvider?.address?.zipcode,
    }
  });

  const handleCloseModal = useCallback(() => {
    onSetVisible(false);
    setEditableProviderId('');
    setNewProviderName('');
  }, [onSetVisible, setEditableProviderId, setNewProviderName]);

  const { handleCreateProvider, handleUpdateProvider } = useSubmitProviderModal({
    onSetVisible,
    setIsLoading,
    createProvider,
    updateProvider,
    handleChangeProvider,
    editableProviderId,
    states,
  });

  return (
    <Modal
      testId={`${editableProvider ? 'edit' : 'add'}-provider`}
      visible
      onSetVisible={handleCloseModal}
      isCloseButtonHidden={isMobile}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box>
          <BackButton
            testId={`${editableProvider ? 'edit' : 'add'}-provider-popup`}
            onClick={handleCloseModal}
            title={editableProvider
              ? `${t('Edit')} ${editableProvider?.name}`
              : t('Add New Retailer/ Provider')}
          />
        </Box>
      ) : (
        <Text size="2xl" textAlign="center" weight={700}>
          {editableProvider
           ? `${t('Edit')} ${editableProvider?.name}`
           : t('Add New Retailer/ Provider')}
        </Text>
      )}

      <Box
        data-testId={`${editableProvider ? 'edit' : 'add'}-provider-form-wrapper_modal`}
        width={{ max: 'none' }}
        margin={{ top: isMobile ? 'spacing16' : 'spacing24', horizontal: isMobile ? '-14px' : undefined }}
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
        round="moduleRound"
      >
        <FormProvider {...methods}>
          <ProviderModalForm states={states} />
        </FormProvider>
      </Box>

      <Box
        margin={{ top: isMobile ? 'auto' : 'spacing24' }}
        direction="row"
        justify="end"
        gap="spacing12"
      >
        <Box flex={isMobile}>
          <AppButton
            testId={`${editableProvider ? 'edit' : 'add'}-provider-modal_cancel`}
            onClick={handleCloseModal}
            buttonType="secondary"
            width={isMobile ? '100%' : '160px'}
          >
            {t('Cancel')}
          </AppButton>
        </Box>
        <Box flex={isMobile}>
          <AppButton
            testId={`${editableProvider ? 'edit' : 'add'}-provider-modal_submit`}
            onClick={editableProvider
              ? methods.handleSubmit(handleUpdateProvider)
              : methods.handleSubmit(handleCreateProvider)}
            width={isMobile ? '100%' : '160px'}
            disabled={isLoading}
          >
            {isLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProviderModal;
